/**
 * Common lookups, chart helpers, shared config resolvers, and remote-student category helpers.
 */
import {
  BookOpen,
  CheckCircle2,
  HelpCircle,
  Info,
  MessageSquare,
  type LucideIcon,
} from 'lucide-react';
import type { StatusBadgeConfig } from '@/components/common/LabelBadge';
import type { BarChartData } from '@/components/common/stats/HorizontalStackedBarChart';
import { USER_SITUATION_CONFIG, type UserSituationConfig } from '@/components/pairs/pair-configs';
import {
  REMOTE_REPORT_SYNTHESIS_STATUS_CONFIG,
  REMOTE_REPORT_TAB_LABELS,
  REMOTE_REPORT_TAB_SESSION_TYPES,
  REMOTE_REPORT_TUTORING_STATUS_CONFIG,
  REPORT_IN_PERSON_TAB_LABELS,
  SYNTHESIS_SESSION_TYPES,
  type PerTypeReportPageActions,
  type ReportInPersonTab,
} from '@/components/reports/report-configs';
import {
  IN_PERSON_STATUS_DISPLAY_ORDER,
  REMOTE_STATUS_DISPLAY_ORDER,
  SESSION_IN_PERSON_STATUS_KEYS_BY_TYPE,
  SESSION_IN_PERSON_TUTORING_STATUS_KEYS,
  SESSION_REMOTE_STATUS_CONFIG,
  SESSION_REMOTE_STATUS_KEYS_BY_TYPE,
  SESSION_STATUS_CONFIG,
  TERMINAL_SESSION_STATUSES,
  type SessionStatusConfig,
} from '@/components/sessions/session-configs';
import {
  SCHOOL_CURRICULUM_LEGACY_MAP,
  SCHOOL_SITUATION_LEGACY_MAP,
  STUDENT_ACCOUNT_STATUS_CONFIG,
  STUDENT_ACTIVE_TAB_STATUS_FILTER_KEYS,
  STUDENT_STATUS_CONFIG,
  type StudentEditStatusSelectContext,
  type StudentRemoteStatusConfig,
  type StudentRemoteStatusFilterKey,
  type StudentRemoteTabView,
  type StudentStatusSelectOption,
} from '@/components/students/student-configs';
import {
  ENGAGEMENT_VALORISATION_OPTIONS,
  TUTOR_ACTIVE_TAB_STATUS_FILTER_KEYS,
  TUTOR_ASSIGNMENT_REFUSAL_REASONS,
  TUTOR_ASSIGNMENT_STATUS_LABEL,
  TUTOR_CRIMINAL_RECORD_STATUS_CONFIG,
  type MilestoneStatus,
  type TutorActiveStatus,
  type TutorPendingStatus,
  type TutorRemoteStatusFilterKey,
  type TutorRemoteTabView,
  type TutorValorisationValue,
} from '@/components/tutors/tutor-configs';
import {
  ASSIGNABILITY_CONFIG,
  ASSIGNABILITY_UNKNOWN,
  ARCHIVE_REASON_OPTIONS,
  CIVIL_TITLE_LABEL,
  COURSE_PATH_LABEL,
  DAY_KEY_TO_AVAILABILITY_API,
  GENDER_APPEARANCE,
  GENDER_LABELS,
  HIGH_SCHOOL_CURRICULUM_LEGACY_MAP,
  INPERSON_EXIT_REASON_LABEL,
  INPERSON_EXIT_REASON_LEGACY_MAP,
  PRO_SITUATION_STATUS_LEGACY_MAP,
  REMOTE_SUBJECT_CONFIG,
  SESSION_IN_PERSON_TAB_LABELS,
  SESSION_TAB_LABELS,
  SESSION_TAB_TYPE_MAP,
  SESSION_TYPE_FILTER_KEYS,
  STUDENT_GENDER_LABELS,
  SUPPORT_TYPE_LABEL,
  type StudentActiveStatus,
  type StudentCategory,
  type StudentPendingStatus,
} from '@/constants/shared-catalog';
import type { AddressSelectionResult } from '@/types/geography.types';
import type {
  SessionStatus,
  SessionTab as SessionInPersonTab,
  SessionType,
} from '@/types/inperson/session.types';
import {
  normalizeStudentAccountStatus,
  type StudentAccountStatus,
} from '@/types/inperson/student.types';
import type { Program } from '@/types/program.types';
import type { ReportTab } from '@/types/remote/report.types';
import type { StudentSituation } from '@/types/remote/student.types';
import type {
  SessionTab as SessionRemoteTab,
  SessionTypeMapping,
} from '@/types/remote/session.types';
import type { TutorSituation } from '@/types/remote/tutor.types';
import {
  normalizeProfileGender,
  normalizeStaffAccountStatus,
  type ArchiveReason,
  type ArchiveRemoteUserDto,
  type FolderSituation,
  type OnboardingStatus,
  type ReactivationPreview,
} from '@/types/user.types';
import { parseLocalDateString, toCalendarDateString } from '@/utils/format-date-utils';
import { getRoleLabel } from '@/utils/role-label-utils';

/** Adult gender label from a stored / API gender value. */
export function getGenderLabel(gender: string | null | undefined): string | null {
  const key = normalizeProfileGender(gender);
  if (!key) return null;
  return GENDER_LABELS[key];
}

/** Student gender label (`Garçon` / `Fille`) from a stored / API gender value. */
export function getStudentGenderLabel(gender: string | null | undefined): string | null {
  const key = normalizeProfileGender(gender);
  if (!key) return null;
  return STUDENT_GENDER_LABELS[key];
}

/** Student gender badge (label + icon + color), or null when gender is unknown. */
export function getStudentGenderConfig(gender: string | null | undefined) {
  const key = normalizeProfileGender(gender);
  if (!key) return null;
  return {
    label: STUDENT_GENDER_LABELS[key],
    icon: GENDER_APPEARANCE[key].icon,
    color: GENDER_APPEARANCE[key].color,
  };
}

/** Civil title (`Monsieur` / `Madame`) from a gender value. */
export function getCivilTitleLabel(value?: string): string {
  if (!value) return '—';
  return CIVIL_TITLE_LABEL[value] ?? value;
}

/** Staff status color token (`success` / `error`) from an account status. */
export function getStaffStatusColor(status: string): 'success' | 'error' {
  return normalizeStaffAccountStatus(status) === 'deactivated' ? 'error' : 'success';
}

/** Weekday keys (`monday`…) to API day numbers (0 = Sunday). */
export function availabilityKeysToDays(keys: string[] | string | undefined): number[] {
  let list: string[];
  if (Array.isArray(keys)) {
    list = keys;
  } else if (typeof keys === 'string') {
    list = [keys];
  } else {
    list = [];
  }
  const set = new Set<number>();
  for (const k of list) {
    const n = DAY_KEY_TO_AVAILABILITY_API[k];
    if (n !== undefined) set.add(n);
  }
  return [...set].sort((a, b) => a - b);
}

/** Assignability badge config; unknown / null falls back to a dash. */
export function getAssignabilityConfig(
  assignability: boolean | 'available' | 'unavailable' | null | undefined,
): StatusBadgeConfig {
  if (assignability == null) return ASSIGNABILITY_UNKNOWN;
  if (assignability === true || assignability === 'available')
    return ASSIGNABILITY_CONFIG.available;
  if (assignability === false || assignability === 'unavailable')
    return ASSIGNABILITY_CONFIG.unavailable;
  return ASSIGNABILITY_UNKNOWN;
}

/** Support-type label (`Distanciel` / `Présentiel` / `Hybride`). */
export function getSupportTypeLabel(value?: string): string {
  if (!value) return '—';
  return SUPPORT_TYPE_LABEL[value] ?? value;
}

/** Course-path label (`Classique` / `Club de sport`). */
export function getCoursePathLabel(value?: string): string {
  if (!value) return '—';
  return COURSE_PATH_LABEL[value] ?? value;
}

/** Normalize a stored French or code exit reason to the catalog code. */
export function normalizeInPersonExitReason(v: string | null | undefined): string {
  if (!v) return '';
  return INPERSON_EXIT_REASON_LEGACY_MAP[v] ?? v;
}

/** French label for an in-person exit reason (legacy labels accepted). */
export function getInPersonExitReasonLabel(v: string | null | undefined): string | null {
  if (!v) return null;
  const normalized = INPERSON_EXIT_REASON_LEGACY_MAP[v] ?? v;
  return INPERSON_EXIT_REASON_LABEL[normalized] ?? v;
}

/** Locale `fr-FR` date, or `—` when empty / unparseable. */
export function formatDeactivationDate(value: Date | string | null | undefined): string {
  const calendar = toCalendarDateString(value);
  if (!calendar) return '—';
  return parseLocalDateString(calendar).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** True when `value` is a known archive-reason code. */
export function isArchiveReason(value: string): value is ArchiveReason {
  return ARCHIVE_REASON_OPTIONS.some((option) => option.value === value);
}

/** Progress-bar color from a 0–100 completion percent. */
export function completionBarColor(pct: number): string {
  if (pct <= 33) return '#9CA3AF';
  if (pct <= 66) return '#F59E0B';
  return '#10B981';
}

/** Completion bucket (`empty` / `partial` / `complete`) from a 0–100 percent. */
export function getCompletionStatus(pct: number): 'empty' | 'partial' | 'complete' {
  if (pct === 0) return 'empty';
  if (pct === 100) return 'complete';
  return 'partial';
}

/** Normalize a camelCase / alias pro-situation value to the catalog code. */
export function normalizeProSituationStatus(v: string | undefined): string {
  if (!v) return '';
  return PRO_SITUATION_STATUS_LEGACY_MAP[v] ?? v;
}

/** Normalize a stored French or code curriculum to the catalog code. */
export function normalizeHighSchoolCurriculum(v: string | undefined): string {
  if (!v) return '';
  return HIGH_SCHOOL_CURRICULUM_LEGACY_MAP[v] ?? v;
}

/** Remote-student list category + status from onboarding / user / pair fields. */
export function getStudentCategory(record: {
  onboardingStatus: string;
  folderSituation: string;
  studentSituation?: StudentSituation | null;
  user?: { status?: string } | null;
  linkedUserId?: string | null;
  _tutorCount?: number;
}): { category: StudentCategory; status: StudentPendingStatus | StudentActiveStatus } {
  const userStatus = record.user?.status;

  if (userStatus === 'suspended') return { category: 'student', status: 'suspended' };
  if (userStatus === 'archived') return { category: 'student', status: 'archived' };
  if (userStatus === 'deleted') return { category: 'student', status: 'deleted' };
  if (userStatus === 'active') {
    const hasTutor =
      record.studentSituation === 'tutoring_in_progress' || (record._tutorCount ?? 0) > 0;
    return {
      category: 'student',
      status: hasTutor ? 'active_with_tutor' : 'active_without_tutor',
    };
  }
  return { category: 'pre_registered', status: 'pending' };
}

/** Remote-subject label, or the raw value when unknown. */
export function getRemoteSubjectLabel(subject: string | null | undefined): string {
  if (!subject) return '—';
  return REMOTE_SUBJECT_CONFIG[subject]?.label ?? subject;
}

/** Maps a raw element_id (from DB `tab-*`) to a {@link SessionTab}. */
export function toSessionTab(rawTabId: string): SessionRemoteTab | null {
  const suffix = rawTabId.startsWith('tab-') ? rawTabId.slice(4) : rawTabId;
  if (suffix in SESSION_TAB_LABELS) return suffix as SessionRemoteTab;
  return null;
}

/** Maps a raw element_id (from DB `tab-*`) to a {@link SessionTab}. */
export function toSessionInPersonTab(rawTabId: string): SessionInPersonTab | null {
  const suffix = rawTabId.startsWith('tab-') ? rawTabId.slice(4) : rawTabId;
  const mapped = suffix === 'information' ? 'information-meeting' : suffix;
  if (mapped in SESSION_IN_PERSON_TAB_LABELS) return mapped as SessionInPersonTab;
  return null;
}

/** Normalizes {@link SESSION_TAB_TYPE_MAP}[tab] to an array (single mapping or list). */
export function getSessionTypesForTab(tab: SessionRemoteTab): SessionTypeMapping[] {
  const mapping = SESSION_TAB_TYPE_MAP[tab];
  return Array.isArray(mapping) ? mapping : [mapping];
}

/** Reverse lookup: session type → remote tab via {@link SESSION_TAB_TYPE_MAP}. */
export function getTabForSessionType(type: SessionType): SessionRemoteTab | null {
  for (const [tab, mapping] of Object.entries(SESSION_TAB_TYPE_MAP)) {
    const types = Array.isArray(mapping) ? mapping : [mapping];
    if (types.some((m) => m.type === type)) {
      return tab as SessionRemoteTab;
    }
  }
  return null;
}

/** Last path segment of a URL, decoded; `document` when empty. */
export function fileNameFromUrl(url: string): string {
  const raw = url.split('/').at(-1)?.split('?')[0] ?? '';
  return decodeURIComponent(raw) || 'document';
}

/** Immutably set a dotted path; missing parents become `{}`. */
export function setNestedPath<T extends Record<string, unknown>>(
  obj: T,
  path: string,
  value: unknown,
): T {
  const keys = path.split('.');
  const updated = { ...obj } as Record<string, unknown>;
  let current: Record<string, unknown> = updated;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    const next = current[key];
    if (!next || typeof next !== 'object' || Array.isArray(next)) {
      current[key] = {};
    }
    current = current[key] as Record<string, unknown>;
  }
  const lastKey = keys.at(-1);
  if (lastKey !== undefined) current[lastKey] = value;
  return updated as T;
}

/** Assign only when the value is not `undefined` / `null` / `''`. */
export function assignTruthy<T extends object>(dto: T, key: string, value: unknown): void {
  if (value !== undefined && value !== null && value !== '') {
    (dto as Record<string, unknown>)[key] = value;
  }
}

/** Assign when the value is not `undefined` (keeps `null` / `''`). */
export function assignDefined<T extends object>(dto: T, key: string, value: unknown): void {
  if (value !== undefined) {
    (dto as Record<string, unknown>)[key] = value;
  }
}

/** Flatten a BAN selection into address / zip / city / country fields. */
export function addressFieldsFromSelection(result: AddressSelectionResult): {
  address: string;
  zipCode: string;
  city: string;
  country?: string;
} {
  return {
    address: result.addressLine1,
    zipCode: result.postalCode,
    city: result.city,
    ...(result.country ? { country: result.country } : {}),
  };
}

/** Split `"Name (code)"` into name + code; null when the suffix is missing. */
export function parseDepartmentFromLabel(
  department: string,
): { code: string; name: string } | null {
  const close = department.lastIndexOf(')');
  if (close !== department.length - 1) return null;
  const open = department.lastIndexOf('(');
  if (open < 0 || open >= close) return null;
  const code = department.slice(open + 1, close);
  if (!code) return null;
  return {
    code,
    name: department.slice(0, open).trim(),
  };
}

/** `first last`, else email, else id, else `fallback`. */
export function formatPersonName(
  person: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    id?: string;
  },
  fallback = '',
): string {
  const full = [person.firstName, person.lastName].filter(Boolean).join(' ').trim();
  if (full) return full;
  if (person.email) return person.email;
  return person.id ?? fallback;
}

/** Archive DTO; `other` is the only reason that keeps the comment. */
export function buildArchiveDto(reason: ArchiveReason, comment: string): ArchiveRemoteUserDto {
  if (reason === 'other') return { reason, comment: comment.trim() };
  return { reason };
}

/** Label current/next status and situation for the reactivate confirm. */
export function formatReactivationPreview(
  preview: ReactivationPreview,
  statusLabel: (status: string) => string,
  situationLabel: (situation: string | null) => string,
): {
  currentStatus: string;
  nextStatus: string;
  currentSituation: string;
  nextSituation: string;
} {
  return {
    currentStatus: statusLabel(preview.currentStatus),
    nextStatus: statusLabel(preview.nextStatus),
    currentSituation: situationLabel(preview.currentSituation),
    nextSituation: situationLabel(preview.nextSituation),
  };
}

/** Height scales with bar count, capped so the chart stays readable. */
export function chartHeightForCount(displayCount: number): number {
  if (displayCount <= 3) return Math.max(160, displayCount * 90 + 60);
  return Math.max(320, Math.min(displayCount * 54 + 100, 620));
}

/** True when the payload has at least one non-zero series value. */
export function hasChartSeriesData(chartData: BarChartData | null): chartData is BarChartData {
  return (
    chartData !== null &&
    chartData.categories.length > 0 &&
    chartData.series.some((series) => series.data.some((value) => value > 0))
  );
}

/** Subtitle count: "N établissement(s)". */
export function chartEstablishmentCountLabel(count: number): string {
  return `${count} établissement${count > 1 ? 's' : ''}`;
}

/** Legacy labels mapped to a canonical tutor valorisation value. */
const VALORISATION_ALIASES: Record<string, TutorValorisationValue> = {
  yes: 'yes',
  oui: 'yes',
  no: 'no',
  non: 'no',
  'pas nécessaire': 'no',
  'pas necessaire': 'no',
  interested: 'interested',
  intéressé: 'interested',
  interesse: 'interested',
  'dont-know': 'dont-know',
  'ne sais pas': 'dont-know',
};

/** Professional-situation bucket keys to scan for valorisation. */
const PROFESSIONAL_SITUATION_BUCKETS = [
  'high_school_student',
  'student',
  'employee',
  'entrepreneur',
  'civil_servant',
  'unemployed',
  'retired',
  'executive',
  'intermediate',
  'worker',
  'farmer',
] as const;

/** Maps stored or legacy labels to a canonical valorisation value. */
export function normalizeTutorValorisation(value: unknown): TutorValorisationValue | undefined {
  if (value === null || value === undefined) return undefined;
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (
    (ENGAGEMENT_VALORISATION_OPTIONS as readonly { value: string }[]).some(
      (option) => option.value === trimmed,
    )
  ) {
    return trimmed as TutorValorisationValue;
  }
  const normalizedKey = trimmed.toLowerCase();
  return VALORISATION_ALIASES[normalizedKey] ?? VALORISATION_ALIASES[trimmed];
}

/** Read valorisation from one professional-situation bucket. */
function readBucketValorisation(bucket: unknown): TutorValorisationValue | undefined {
  if (!bucket || typeof bucket !== 'object') return undefined;
  const record = bucket as Record<string, unknown>;
  return (
    normalizeTutorValorisation(record.isEngagementValorised) ??
    normalizeTutorValorisation(record.associativeEngagementValued) ??
    normalizeTutorValorisation(record.associativeEngagement)
  );
}

/** First valorisation found across situation buckets. */
function readProfessionalSituationValorisation(
  professionalSituation: unknown,
): TutorValorisationValue | undefined {
  if (!professionalSituation || typeof professionalSituation !== 'object') return undefined;
  const situation = professionalSituation as Record<string, unknown>;
  for (const bucketKey of PROFESSIONAL_SITUATION_BUCKETS) {
    const resolved = readBucketValorisation(situation[bucketKey]);
    if (resolved) return resolved;
  }
  return undefined;
}

/** Mirrors backend `resolveTutorValorisation` for form prefill. */
export function resolveTutorValorisationFromSections(
  sections: Record<string, unknown>,
): TutorValorisationValue | undefined {
  const mySupport = sections.my_support as Record<string, unknown> | undefined;
  const myInformation = sections.my_information as Record<string, unknown> | undefined;
  const direct = normalizeTutorValorisation(mySupport?.valorisation);
  if (direct) return direct;
  const fromProfessionalSituation = readProfessionalSituationValorisation(
    myInformation?.professionalSituation,
  );
  if (fromProfessionalSituation) return fromProfessionalSituation;
  return normalizeTutorValorisation(myInformation?.isYourCommitmentValuedByYourInstitution);
}

/** Resolves criminal-record status badge config (defaults to pending). */
export function getTutorCriminalRecordStatusConfig(status: string | undefined) {
  return (
    TUTOR_CRIMINAL_RECORD_STATUS_CONFIG[status ?? ''] ?? TUTOR_CRIMINAL_RECORD_STATUS_CONFIG.pending
  );
}

/** Resolves the assignment status label. */
export function getTutorAssignmentStatusLabel(status: string | undefined): string {
  if (!status) return '';
  return TUTOR_ASSIGNMENT_STATUS_LABEL[status] ?? status;
}

/** Resolves the assignment status color token. */
export function getTutorAssignmentStatusColor(status: string | undefined): string {
  if (status === 'validated') return 'rgb(var(--status-success))';
  if (status?.includes('refused')) return 'rgb(var(--status-danger))';
  return 'rgb(var(--status-pending))';
}

/** Status-filter keys for the given remote tutor tab. */
export function getTutorRemoteStatusFilterKeys(
  activeTab: TutorRemoteTabView,
): readonly TutorRemoteStatusFilterKey[] {
  if (activeTab === 'pre_registered') return ['pending'];
  if (activeTab === 'suspended') return ['suspended'];
  if (activeTab === 'archived') return ['archived'];
  return TUTOR_ACTIVE_TAB_STATUS_FILTER_KEYS;
}

/** Derives remote tab and status from `user.status`. */
export function getTutorRemoteBusinessStatus(record: {
  onboardingStatus: OnboardingStatus;
  folderSituation: FolderSituation;
  tutorSituation?: TutorSituation | null;
  user?: { status?: string };
  _binomeCount?: number;
}): { tab: TutorRemoteTabView; status: TutorPendingStatus | TutorActiveStatus } {
  const userStatus = record.user?.status;
  if (userStatus === 'suspended') return { tab: 'suspended', status: 'suspended' };
  if (userStatus === 'archived') return { tab: 'archived', status: 'archived' };
  if (userStatus === 'deleted') return { tab: 'tutors', status: 'deleted' };
  if (userStatus === 'active') {
    const hasPair =
      record.tutorSituation === 'tutoring_in_progress' || (record._binomeCount ?? 0) > 0;
    return { tab: 'tutors', status: hasPair ? 'active_in_progress' : 'active_awaiting_student' };
  }
  return { tab: 'pre_registered', status: 'pending' };
}

/** Resolves the situation badge shown next to the status. */
export function getTutorSituation(record: {
  onboardingStatus: OnboardingStatus;
  tutorSituation?: TutorSituation | null;
  _binomeCount?: number;
}): TutorSituation {
  if (record.tutorSituation != null) return record.tutorSituation;
  if (record.onboardingStatus === 'active') {
    return (record._binomeCount ?? 0) > 0 ? 'tutoring_in_progress' : 'awaiting_student';
  }
  return 'registration_in_progress';
}

/** Completion percentage on a registration section, if present. */
function sectionCompletionPercent(section: Record<string, unknown>): number {
  const completion = section.sectionCompletion as { completionPercentage?: number } | undefined;
  return completion?.completionPercentage ?? 0;
}

/** Resolves a remote tutor milestone status from sections. */
export function getTutorRemoteMilestoneStatus(
  sections: Record<string, Record<string, unknown>>,
  milestoneKey: string,
): MilestoneStatus {
  const section = sections[milestoneKey];
  if (!section) return 'locked';
  if (milestoneKey === 'information_session') {
    if (section.hasParticipated === true) return 'validated';
    return Object.keys(section).length > 0 ? 'pending' : 'locked';
  }
  const pct = sectionCompletionPercent(section);
  if (pct >= 100) return 'validated';
  if (Object.keys(section).length > 0) return 'pending';
  return 'locked';
}

/** Milestone status from the criminal-record section. */
function getMyDocumentsMilestoneStatus(section: Record<string, unknown>): MilestoneStatus {
  const criminalRecord = section.criminalRecord as { validationStatus?: string } | undefined;
  const status = criminalRecord?.validationStatus;
  if (status === 'validated') return 'validated';
  if (status) return 'pending';
  return Object.keys(section).length > 0 ? 'pending' : 'locked';
}

/** Resolves an in-person tutor milestone status from sections. */
export function getTutorInPersonMilestoneStatus(
  sections: Record<string, Record<string, unknown>>,
  milestoneKey: string,
): MilestoneStatus {
  const section = sections[milestoneKey];
  if (!section) return 'locked';
  if (milestoneKey === 'information_session') {
    return section.hasParticipated === true ? 'validated' : 'pending';
  }
  if (milestoneKey === 'my_documents') return getMyDocumentsMilestoneStatus(section);
  const pct = sectionCompletionPercent(section);
  if (pct >= 100) return 'validated';
  if (Object.keys(section).length > 0) return 'pending';
  return 'locked';
}

/** Formats years of engagement (`3 ans`) from a number or leading digits. */
export function formatYearsOfEngagement(value?: string | number): string {
  if (value === undefined || value === null || value === '') return '—';
  if (typeof value === 'number') return `${value} an${value > 1 ? 's' : ''}`;
  const match = /^(\d+)/.exec(String(value));
  if (match) {
    const years = Number.parseInt(match[1], 10);
    return `${years} an${years > 1 ? 's' : ''}`;
  }
  return String(value).replaceAll('-', ' ');
}

/** Assignment-refusal label, appending a custom description when required. */
export function formatAssignmentRefusalReason(
  reasonCode: string,
  customDescription: string,
): string {
  const config = TUTOR_ASSIGNMENT_REFUSAL_REASONS.find((reason) => reason.code === reasonCode);
  if (config?.requiresDescription && customDescription.trim()) {
    return `${config.label} : ${customDescription.trim()}`;
  }
  return config?.label ?? customDescription.trim();
}

/** Header title and icon for the assignment decision panel. */
export function getAssignmentDecisionHeader(showBothSections: boolean, isPrevalidate: boolean) {
  if (showBothSections) {
    return {
      title: 'Gérer les affectations',
      icon: CheckCircle2,
      iconColor: 'rgb(var(--color-1-nuance-1))',
      iconBgColor: 'color-mix(in srgb, rgb(var(--color-1-nuance-1)) 15%, transparent)',
    };
  }
  if (isPrevalidate) {
    return {
      title: 'Gérer les affectations (recruteur)',
      icon: Info,
      iconColor: 'rgb(var(--color-2-nuance-1))',
      iconBgColor: 'color-mix(in srgb, rgb(var(--color-2-nuance-1)) 15%, transparent)',
    };
  }
  return {
    title: 'Gérer les affectations (coordinateur)',
    icon: CheckCircle2,
    iconColor: 'rgb(var(--status-success))',
    iconBgColor: 'color-mix(in srgb, rgb(var(--status-success)) 15%, transparent)',
  };
}

/** Maps a student status key to a detail color token name. */
export function getStudentStatusDetailColorName(
  statusKey: string,
): 'pending' | 'success' | 'error' | 'color-2' {
  if (statusKey === 'deactivated') return 'error';
  if (statusKey === 'registered') return 'color-2';
  return 'success';
}

/** Catalog label for an in-person student account status. */
function studentStatusLabel(key: StudentAccountStatus): string {
  return STUDENT_STATUS_CONFIG[key]?.label ?? key;
}

/** Previous status before the latest `deactivated` row (`createdAt` descending). */
export function resolvePreviousStatusBeforeDeactivate(
  rows: Array<{ oldStatus: string | null; newStatus: string }>,
): StudentAccountStatus | null {
  for (const row of rows) {
    if (normalizeStudentAccountStatus(String(row.newStatus)) !== 'deactivated') continue;
    if (row.oldStatus == null) return null;
    const prev = normalizeStudentAccountStatus(String(row.oldStatus));
    if (prev === 'registered' || prev === 'accompanied') return prev;
    return null;
  }
  return null;
}

/** Restricted Status select options on the in-person student edit form. */
export function getStudentEditStatusSelectOptions(
  currentStatus: StudentAccountStatus,
  context?: StudentEditStatusSelectContext,
): StudentStatusSelectOption[] {
  if (currentStatus === 'registered') {
    return [
      { value: 'registered', label: studentStatusLabel('registered') },
      { value: 'deactivated', label: studentStatusLabel('deactivated') },
    ];
  }
  if (currentStatus === 'accompanied') {
    return [
      { value: 'accompanied', label: studentStatusLabel('accompanied') },
      { value: 'deactivated', label: studentStatusLabel('deactivated') },
    ];
  }
  const fromHistory = context?.previousStatusBeforeDeactivate ?? null;
  const fromApi = context?.reactivationTargetStatus ?? null;
  const fromHistoryIfValid =
    fromHistory === 'accompanied' || fromHistory === 'registered' ? fromHistory : null;
  const effective: StudentAccountStatus | null =
    fromHistoryIfValid ?? (fromApi === 'accompanied' || fromApi === 'registered' ? fromApi : null);
  if (effective === 'accompanied') {
    return [
      { value: 'accompanied', label: studentStatusLabel('accompanied') },
      { value: 'deactivated', label: studentStatusLabel('deactivated') },
    ];
  }
  if (effective === 'registered') {
    return [
      { value: 'registered', label: studentStatusLabel('registered') },
      { value: 'deactivated', label: studentStatusLabel('deactivated') },
    ];
  }
  return [
    { value: 'registered', label: studentStatusLabel('registered') },
    { value: 'accompanied', label: studentStatusLabel('accompanied') },
    { value: 'deactivated', label: studentStatusLabel('deactivated') },
  ];
}

/** Account-level remote student status chip (pairs collapsed to `active`). */
export function getStudentAccountStatusConfig(
  status: StudentPendingStatus | StudentActiveStatus,
): StudentRemoteStatusConfig {
  if (status === 'active_with_tutor' || status === 'active_without_tutor') {
    return STUDENT_ACCOUNT_STATUS_CONFIG.active;
  }
  return STUDENT_ACCOUNT_STATUS_CONFIG[status] ?? STUDENT_ACCOUNT_STATUS_CONFIG.pending;
}

/** Resolves the situation badge shown next to the status. */
export function getStudentSituation(record: {
  onboardingStatus: string;
  studentSituation?: StudentSituation | null;
  _tutorCount?: number;
}): StudentSituation {
  if (record.studentSituation != null) return record.studentSituation;
  if (record.onboardingStatus === 'active') {
    return (record._tutorCount ?? 0) > 0 ? 'tutoring_in_progress' : 'awaiting_tutor';
  }
  return 'registration_in_progress';
}

/** Derives the remote student tab from `user.status` and situation. */
export function getStudentRemoteTabView(record: {
  onboardingStatus: string;
  folderSituation: string;
  studentSituation?: StudentSituation | null;
  validation?: { isValidated?: boolean } | null;
  sections: Record<string, unknown>;
  user?: { status?: string } | null;
}): StudentRemoteTabView {
  const userStatus = record.user?.status;
  if (userStatus === 'suspended') return 'suspended';
  if (userStatus === 'archived') return 'archived';
  if (record.studentSituation != null) {
    return record.studentSituation === 'registration_in_progress' ? 'pre_registered' : 'students';
  }
  return record.onboardingStatus === 'prospect' ? 'pre_registered' : 'students';
}

/** Registration milestone chip from section completion or leftover keys. */
export function getStudentMilestoneStatus(
  sections: Record<string, unknown>,
  milestoneKey: string,
): 'locked' | 'pending' | 'validated' | 'rejected' {
  const section = sections[milestoneKey] as Record<string, unknown> | undefined;
  if (!section) return 'locked';
  const completion = section.sectionCompletion as { completionPercentage?: number } | undefined;
  if (completion?.completionPercentage !== undefined) {
    if (completion.completionPercentage === 100) return 'validated';
    if (completion.completionPercentage > 0) return 'pending';
    return 'locked';
  }
  const dataKeys = Object.keys(section).filter(
    (key) => key !== 'sectionCompletion' && key !== 'updatedAt',
  );
  if (dataKeys.length === 0) return 'locked';
  return 'pending';
}

/** Maps stored or legacy school-situation labels to a canonical value. */
export function normalizeSchoolSituation(v: string | undefined): string {
  if (!v) return '';
  return SCHOOL_SITUATION_LEGACY_MAP[v] ?? v;
}

/** Maps stored or legacy school-curriculum labels to a canonical value. */
export function normalizeSchoolCurriculum(v: string | undefined): string {
  if (!v) return '';
  return SCHOOL_CURRICULUM_LEGACY_MAP[v] ?? v;
}

/** Status-filter keys for the given remote student tab. */
export function getStudentRemoteStatusFilterKeys(
  activeTab: StudentRemoteTabView,
): readonly StudentRemoteStatusFilterKey[] {
  if (activeTab === 'pre_registered') return ['pending'];
  if (activeTab === 'suspended') return ['suspended'];
  if (activeTab === 'archived') return ['archived'];
  return STUDENT_ACTIVE_TAB_STATUS_FILTER_KEYS;
}

/** Display config for a tutor/student situation. Unknown values get a neutral badge. */
export function getUserSituationConfig(
  situation: string | null | undefined,
): UserSituationConfig | null {
  if (!situation?.trim()) return null;
  const key = situation.trim().toLowerCase();
  return (
    USER_SITUATION_CONFIG[key] ?? {
      label: situation,
      icon: HelpCircle,
      badgeVariant: 'ghost',
      badgeColor: 'rgb(var(--status-neutral))',
    }
  );
}

/** Tailwind text color from an absence rate. */
export function getAbsenceRateColor(rate: number): string {
  if (rate < 33) return 'text-green-600';
  if (rate < 66) return 'text-yellow-600';
  return 'text-red-600';
}

/** Excel header row for an in-person pair export. */
export function getPairExportHeaders(): string[] {
  const student = getRoleLabel('student');
  const tutor = getRoleLabel('tutor');
  return [
    student,
    `Email ${student}`,
    tutor,
    `Email ${tutor}`,
    'Type',
    'Séance',
    'Date',
    'Heure',
    'Établissement',
    'Matière',
  ];
}

/** Program name from the row, else the catalog label. */
export function getProgramDisplayName(
  program: { programId: string; programName?: string | null },
  programsList: Program[],
): string {
  if (program.programName) return program.programName;
  const found = programsList.find((item) => item.id === program.programId);
  if (found) return found.label || found.name || 'Programme';
  return 'Programme';
}

/** Capitalizes a weekday availability label. */
export function formatAvailabilityDay(day: string): string {
  if (!day) return '';
  return day.charAt(0).toUpperCase() + day.slice(1).toLowerCase();
}

/** Compact weekday abbreviation plus start–end times. */
export function formatSlotLabel(slot: {
  fullName: string;
  startTime: string;
  endTime: string;
}): string {
  const dayAbbrev = (slot.fullName ?? '').split(' ')[0]?.slice(0, 3) ?? '';
  return `${dayAbbrev} ${slot.startTime}-${slot.endTime}`;
}

/** Maps a raw element_id (from DB `tab-*`) to an in-person report tab. */
export function toReportInPersonTab(rawTabId: string): ReportInPersonTab | null {
  const id = rawTabId.startsWith('tab-') ? rawTabId.slice(4) : rawTabId;
  const mapped = id === 'information' ? 'information-meeting' : id;
  if (mapped in REPORT_IN_PERSON_TAB_LABELS) return mapped as ReportInPersonTab;
  return null;
}

/** Maps a session type string to its report tab. Handles remote and in-person types. */
export function sessionTypeToReportTab(sessionType: string): ReportTab {
  if (sessionType === 'individual_tutoring') return 'tutoring';
  if (sessionType === 'information_meeting') return 'information-meeting';
  if (sessionType === 'information_meeting_in_person') return 'information-meeting';
  if (sessionType === 'welcome_meeting') return 'welcome-meeting';
  if (sessionType === 'workshop') return 'field-event';
  if (['homework_help', 'group_tutoring', 'quick_help_desk'].includes(sessionType))
    return 'tutoring';
  return 'all';
}

/** Maps a raw element_id (from DB `tab-*`) to a remote report tab. */
export function toReportTab(rawTabId: string): ReportTab | null {
  const suffix = rawTabId.startsWith('tab-') ? rawTabId.slice(4) : rawTabId;
  if (suffix in REMOTE_REPORT_TAB_LABELS) return suffix as ReportTab;
  return null;
}

/** Per-type edit/validate flags for a report row. */
export function resolveReportPermissions(
  sessionType: string,
  pageActions: PerTypeReportPageActions | undefined,
): { canEdit: boolean; canValidate: boolean } {
  const tab = sessionTypeToReportTab(sessionType);
  const perTypeEdit = {
    tutoring: pageActions?.canEditRowTutoring,
    'information-meeting': pageActions?.canEditRowInformationMeeting,
    'welcome-meeting': pageActions?.canEditRowWelcome,
    'field-event': pageActions?.canEditRowFieldEvent,
    all: undefined,
  }[tab];
  const perTypeValidate = {
    tutoring: pageActions?.canValidateTutoring,
    'information-meeting': pageActions?.canValidateInformationMeeting,
    'welcome-meeting': pageActions?.canValidateWelcome,
    'field-event': pageActions?.canValidateFieldEvent,
    all: undefined,
  }[tab];
  return {
    canEdit: perTypeEdit ?? false,
    canValidate: perTypeValidate ?? false,
  };
}

/** Effective session types for a report tab and the selected type chips. */
export function getEffectiveReportSessionTypes(
  activeTab: ReportTab,
  sessionTypes: string[],
): string[] {
  if (activeTab !== 'all') {
    return REMOTE_REPORT_TAB_SESSION_TYPES[activeTab] ?? [];
  }
  return sessionTypes.length > 0 ? sessionTypes : ['individual_tutoring'];
}

/** Report status keys for the given session types. */
export function getReportStatusKeys(effectiveTypes: string[]): string[] {
  const hasTutoring = effectiveTypes.some((type) => !SYNTHESIS_SESSION_TYPES.has(type));
  const hasSynthesis = effectiveTypes.some((type) => SYNTHESIS_SESSION_TYPES.has(type));
  if (hasTutoring && hasSynthesis) {
    return [
      ...Object.keys(REMOTE_REPORT_TUTORING_STATUS_CONFIG),
      ...Object.keys(REMOTE_REPORT_SYNTHESIS_STATUS_CONFIG),
    ];
  }
  if (hasTutoring) return Object.keys(REMOTE_REPORT_TUTORING_STATUS_CONFIG);
  return Object.keys(REMOTE_REPORT_SYNTHESIS_STATUS_CONFIG);
}

/** Icon for a remote report schema. */
export function reportIcon(schemaId: string): LucideIcon {
  if (schemaId === 'cr_tutorat') return BookOpen;
  return MessageSquare;
}

/** Human label for a session format. */
export function getSessionFormatLabel(format: string | undefined): string {
  if (format === 'in_person') return '📍 Présentiel';
  if (format === 'remote') return '💻 Distanciel';
  if (format === 'hybrid') return '🔄 Hybride';
  return 'Non spécifié';
}

/** Ordered union of in-person statuses for the selected types. */
export function getInPersonStatusKeysForTypes(types: string[]): SessionStatus[] {
  const effective = types.length > 0 ? types : [...SESSION_TYPE_FILTER_KEYS];
  const set = new Set<SessionStatus>();
  for (const type of effective) {
    for (const status of SESSION_IN_PERSON_STATUS_KEYS_BY_TYPE[type] ??
      SESSION_IN_PERSON_TUTORING_STATUS_KEYS) {
      set.add(status);
    }
  }
  return IN_PERSON_STATUS_DISPLAY_ORDER.filter((status) => set.has(status));
}

/** COMPLETED is stored as-is; PARTICIPANTS_ABSENT displays as Non réalisée. */
export function getInPersonStatusConfig(
  status: string,
  sessionType?: string | null,
): SessionStatusConfig {
  const key = status as SessionStatus;
  if (key === 'PARTICIPANTS_ABSENT') {
    return SESSION_STATUS_CONFIG.NOT_COMPLETED;
  }
  const base = SESSION_STATUS_CONFIG[key] ?? SESSION_STATUS_CONFIG.PLANNED;
  if (sessionType === 'information_meeting_in_person' && key === 'COMPLETED') {
    return { ...base, label: 'Réalisée' };
  }
  return base;
}

/** Remote status chip config; PARTICIPANTS_ABSENT displays as Non réalisée. */
export function getEffectiveStatusConfig(status: SessionStatus): SessionStatusConfig {
  if (status === 'PARTICIPANTS_ABSENT') {
    return SESSION_REMOTE_STATUS_CONFIG.NOT_COMPLETED;
  }
  return SESSION_REMOTE_STATUS_CONFIG[status] || SESSION_REMOTE_STATUS_CONFIG.PLANNED;
}

/** UI "Non réalisée" also matches backend PARTICIPANTS_ABSENT on information meetings. */
export function expandNotCompletedSessionStatuses(
  statuses: string[] | undefined,
): string[] | undefined {
  if (!statuses || statuses.length === 0) return statuses;
  if (!statuses.includes('NOT_COMPLETED')) return statuses;
  if (statuses.includes('PARTICIPANTS_ABSENT')) return statuses;
  return [...statuses, 'PARTICIPANTS_ABSENT'];
}

/** Ordered union of remote statuses for the selected types. */
export function getRemoteStatusKeysForTypes(types: string[]): SessionStatus[] {
  const effective = types.length > 0 ? types : (['individual_tutoring'] as string[]);
  const set = new Set<SessionStatus>();
  for (const type of effective) {
    for (const status of SESSION_REMOTE_STATUS_KEYS_BY_TYPE[type] ?? []) {
      set.add(status);
    }
  }
  return REMOTE_STATUS_DISPLAY_ORDER.filter((status) => set.has(status));
}

/** True when the session status can no longer be modified. */
export function isTerminalStatus(status: SessionStatus): boolean {
  return TERMINAL_SESSION_STATUSES.includes(status);
}
